import { useCallback, useState } from 'react';
import { useTranslation } from '@bonanza-social/i18n';
import {
  Box,
  Button,
  Icon,
  Input,
  PressableIcon,
  Text,
} from '@bonanza-social/moonlight';
import { selectAccount, updateScreenName } from '../../state';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { BottomSheet } from '@bonanza-social/navigation';
import { ApiError } from '@bonanza-social/toolkit/api';
import { screenNameErrorMap, ScreenNameSchema } from './ScreenName.constants';
import { AccountService } from '../../api';

function ScreenName() {
  const dispatch = useDispatch();
  const [t] = useTranslation('account');
  const [isEditing, setIsEditing] = useState(false);
  const [localScreenName, setLocalScreenName] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const account = useSelector(selectAccount);
  const screenName = account?.personalDetails?.screenName;

  const handleInfoPress = () => {
    BottomSheet.open({
      namespace: 'account',
      type: 'ScreenNameInfo',
    });
  };

  const handleEditPress = () => {
    setLocalScreenName(screenName ?? '');
    setIsEditing(true);
  };

  const handleCancelPress = () => {
    setError('');
    setLocalScreenName('');
    setIsEditing(false);
  };

  const handleTextChange = useCallback((text: string) => {
    setLocalScreenName(text);
    setError('');
  }, []);

  const onSubmit = useCallback(async () => {
    if (localScreenName === screenName) {
      setIsEditing(false);
      return;
    }

    const { error } = ScreenNameSchema.safeParse(localScreenName);

    if (error && error.errors[0].message) {
      return setError(error.errors[0].message);
    }

    try {
      setIsSaving(true);
      await AccountService.updateScreenName({
        data: {
          ScreenName: localScreenName,
        },
      });
      dispatch(updateScreenName(localScreenName));
      handleCancelPress();
    } catch (error) {
      if (error instanceof ApiError) {
        const { errorCode } = error.data || {};

        if (errorCode && errorCode in screenNameErrorMap) {
          setError(
            t(screenNameErrorMap[errorCode as keyof typeof screenNameErrorMap]),
          );
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, localScreenName, screenName, t]);

  return (
    <Box
      testID='screen-name'
      justifyContent="center"
      alignItems="center"
      backgroundColor="secondaryBackgroundHover"
      padding="space-400"
      borderRadius="radius-200"
      gap="space-200"
      minWidth="100%"
    >
      <Box flexDirection="row" alignItems="center" gap="space-200">
        <Text
          variant="body-s-semi"
          color="darkSurfacePrimary"
          locKey="screenName"
          locNS="account"
        />
        <PressableIcon
          name="info"
          width="space-400"
          height="space-400"
          color="darkSurfacePrimary"
          onPress={handleInfoPress}
        />
      </Box>
      {isEditing ? (
        <Box width="100%">
          <Input
            placeholderKey="screenName"
            placeholderNS="account"
            value={localScreenName}
            error={error}
            onChangeText={handleTextChange}
            backgroundColor="transparent"
            borderWidth="border-width-0"
            borderBottomWidth="border-width-050"
            borderColor={error ? 'borderError' : 'borderGoldSolid'}
            borderRadius="radius-0"
            autoFocus
          />
          <Box flexDirection="row" gap="space-100">
            {error && (
              <Icon
                name="info"
                color="darkSurfaceError"
                width="space-300"
                height="space-300"
                marginTop="space-100"
              />
            )}
            <Text
              variant="caption"
              color={error ? 'darkSurfaceError' : 'darkSurfaceMuted'}
              // @ts-expect-error - error is not always defined
              locKey={error || 'screenNamePlaceholder'}
              locNS="account"
              marginTop="space-050"
              flex={1}
            />
          </Box>
          <Box flexDirection="row" gap="space-400" marginTop="space-400">
            <Button
              variant="secondary"
              onPress={handleCancelPress}
              locNS="account"
              locKey="cancel"
              flex={1}
            />
            <Button
              variant="primary"
              onPress={onSubmit}
              locNS="account"
              locKey="saveName"
              loading={isSaving}
              flex={1}
            />
          </Box>
        </Box>
      ) : (
        <Box flexDirection="row" alignItems="center" gap="space-200">
          <Text variant="h6" gradient="darkSurfaceAccent">
            {screenName}
          </Text>
          <PressableIcon
            name="edit"
            gradient="accentBackground"
            onPress={handleEditPress}
          />
        </Box>
      )}
    </Box>
  );
}

export default ScreenName;
